import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import {
  type AddCartItemPayload,
  cartItemApi,
  type UpdateCartItemPayload,
} from '@/api/commercial/cart-items';
import { catalogApi } from '@/api/commercial/catalog';
import { PriceTableStatus, priceTableApi } from '@/api/commercial/price-tables';
import { formatCents, formatDataAmount } from '@/lib/formatters';
import { type CartItemFormData, cartItemSchema } from '@/schemas/commercial/cart-item.schema';
import {
  type ConnectivityOption,
  type ICatalogItem,
  type IProposalCartItem,
  NegotiationType,
} from '@/types/commercial/cart';

export function useCartItemForm(proposalId?: string, editingItem?: IProposalCartItem | null) {
  const queryClient = useQueryClient();
  const isEditMode = !!editingItem;

  const form = useForm<CartItemFormData>({
    resolver: zodResolver(cartItemSchema),
    defaultValues: {
      negotiationType: '',
      catalogItemId: '',
      quantity: 0,
      connectivityId: '',
    },
  });

  const { setValue, watch, reset } = form;

  const negotiationType = watch('negotiationType');
  const catalogItemId = watch('catalogItemId');
  const connectivityId = watch('connectivityId');

  const hasNegotiationType = negotiationType !== '';
  const isSaleWithSimCard = negotiationType === NegotiationType.SALE_WITH_SIM_CARD;

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional reset on type change
  useEffect(() => {
    if (isEditMode) return;
    setValue('catalogItemId', '', { shouldValidate: false });
    setValue('quantity', 0, { shouldValidate: false });
    setValue('connectivityId', '', { shouldValidate: false });
  }, [negotiationType]);

  useEffect(() => {
    if (!editingItem) return;
    reset({
      negotiationType: editingItem.negotiationType,
      catalogItemId: editingItem.productId,
      quantity: editingItem.quantity,
      connectivityId: '',
      discountPercent: editingItem.discountPercent,
    });
  }, [editingItem, reset]);

  const { data: catalogData, isLoading: catalogLoading } = useQuery({
    queryKey: ['catalog', negotiationType],
    queryFn: () => catalogApi.findMany({ negotiationType: negotiationType as NegotiationType }),
    enabled: hasNegotiationType,
  });

  const catalogItems: ICatalogItem[] = useMemo(
    () =>
      (catalogData ?? []).filter(
        (item) =>
          item.basePriceCashInCents > 0 ||
          item.basePriceInstallmentInCents > 0 ||
          item.basePriceMonthlyInCents > 0,
      ),
    [catalogData],
  );

  const { data: activePriceTable, isLoading: priceTableLoading } = useQuery({
    queryKey: ['price-tables', 'active'],
    queryFn: async () => {
      const result = await priceTableApi.findMany({
        status: PriceTableStatus.ACTIVE,
        limit: 1,
      });
      return result.docs[0] ?? null;
    },
    enabled: isSaleWithSimCard,
  });

  const isSimCard = negotiationType === NegotiationType.SIM_CARD;
  const isServices = negotiationType === NegotiationType.SERVICES;

  const connectivityOptions: ConnectivityOption[] = useMemo(() => {
    if (!activePriceTable?.simcardPayment) return [];
    return activePriceTable.simcardPayment.map((sim, index) => ({
      id: `${index}`,
      name: `${sim.carriers.join('/')} - ${formatDataAmount(sim.dataAmountMb)} - ${sim.planType}`,
      priceInBundleInCents: sim.priceInBundleInCents,
      priceWithoutDeviceInCents: sim.priceWithoutDeviceInCents,
      details: {
        carrier: sim.carriers[0] ?? '',
        dataAmountMb: sim.dataAmountMb,
        planType: sim.planType,
        provider: sim.provider,
      },
    }));
  }, [activePriceTable]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: match only when options become available
  useEffect(() => {
    if (
      !isEditMode ||
      !isSaleWithSimCard ||
      !editingItem?.connectivityDetails ||
      connectivityOptions.length === 0
    )
      return;

    const conn = editingItem.connectivityDetails;
    const matched = connectivityOptions.find(
      (opt) =>
        opt.details.carrier === conn.carrier &&
        opt.details.dataAmountMb === conn.dataAmountMb &&
        opt.details.planType === conn.planType &&
        opt.details.provider === conn.provider,
    );
    if (matched) {
      setValue('connectivityId', matched.id, { shouldValidate: false });
    }
  }, [isEditMode, isSaleWithSimCard, editingItem, connectivityOptions]);

  const selectedCatalogItem = catalogItems.find((item) => item.id === catalogItemId) ?? null;

  const selectedConnectivity = connectivityOptions.find((c) => c.id === connectivityId) ?? null;

  const priceInstallmentDisplay = formatCents(selectedCatalogItem?.basePriceInstallmentInCents);
  const priceUpfrontDisplay = formatCents(selectedCatalogItem?.basePriceCashInCents);
  const priceYearlyDisplay = formatCents(selectedCatalogItem?.service?.yearlyPriceInCents);
  const priceFixedDisplay = formatCents(selectedCatalogItem?.service?.fixedPriceInCents);
  const priceMonthlyDisplay = formatCents(
    isSimCard
      ? selectedCatalogItem?.simCard?.priceWithoutDeviceInCents
      : selectedCatalogItem?.basePriceMonthlyInCents,
  );
  const connectivityPriceDisplay = selectedConnectivity
    ? formatCents(selectedConnectivity.priceInBundleInCents)
    : '-';

  const addMutation = useMutation({
    mutationFn: (payload: AddCartItemPayload) => {
      if (!proposalId) throw new Error('proposalId is required');
      return cartItemApi.addOne(proposalId, payload);
    },
    onSuccess: () => {
      if (proposalId) {
        queryClient.invalidateQueries({ queryKey: ['cart-items', proposalId] });
        queryClient.invalidateQueries({ queryKey: ['cart-calculate', proposalId] });
        queryClient.invalidateQueries({ queryKey: ['proposal-configurations', proposalId] });
      }
    },
  });

  const updateMutation = useMutation({
    mutationFn: (payload: UpdateCartItemPayload) => {
      if (!proposalId || !editingItem) throw new Error('proposalId and editingItem are required');
      return cartItemApi.updateOne(proposalId, editingItem.id, payload);
    },
    onSuccess: () => {
      if (proposalId) {
        queryClient.invalidateQueries({ queryKey: ['cart-items', proposalId] });
        queryClient.invalidateQueries({ queryKey: ['cart-calculate', proposalId] });
        queryClient.invalidateQueries({ queryKey: ['proposal-configurations', proposalId] });
      }
    },
  });

  const buildPayload = (): AddCartItemPayload | null => {
    if (!selectedCatalogItem || !negotiationType) return null;

    const base = {
      negotiationType: negotiationType as NegotiationType,
      quantity: form.getValues('quantity') || 1,
    };

    switch (negotiationType) {
      case NegotiationType.SALE_WITHOUT_SIM_CARD:
        return { ...base, productId: selectedCatalogItem.id };

      case NegotiationType.SALE_WITH_SIM_CARD:
        return {
          ...base,
          productId: selectedCatalogItem.id,
          connectivityDetails: selectedConnectivity?.details,
        };

      case NegotiationType.SIM_CARD:
        return {
          ...base,
          connectivityId: selectedCatalogItem.id,
          connectivityDetails: selectedCatalogItem.simCard
            ? {
                carrier: selectedCatalogItem.simCard.carriers[0] ?? '',
                dataAmountMb: selectedCatalogItem.simCard.dataAmountMb,
                planType: selectedCatalogItem.simCard.planType,
                provider: selectedCatalogItem.simCard.provider,
              }
            : undefined,
        };

      case NegotiationType.ACCESSORY_SALE:
        return { ...base, accessoryId: selectedCatalogItem.id };

      case NegotiationType.SERVICES:
        return { ...base, serviceId: selectedCatalogItem.id };

      default:
        return null;
    }
  };

  const buildUpdatePayload = (): UpdateCartItemPayload | null => {
    if (!negotiationType) return null;

    return {
      negotiationType: negotiationType as NegotiationType,
      quantity: form.getValues('quantity') || 1,
      discountPercent: form.getValues('discountPercent') ?? 0,
    };
  };

  const resetForm = () => {
    reset({
      negotiationType: '',
      catalogItemId: '',
      quantity: 0,
      connectivityId: '',
    });
  };

  const resetItemFields = () => {
    setValue('catalogItemId', '', { shouldValidate: false });
    setValue('quantity', 0, { shouldValidate: false });
    setValue('connectivityId', '', { shouldValidate: false });
  };

  return {
    form,
    negotiationType,
    hasNegotiationType,
    isEditMode,
    isSaleWithSimCard,
    isSimCard,
    isServices,
    catalogItems,
    catalogLoading,
    connectivityOptions,
    connectivityLoading: priceTableLoading,
    selectedCatalogItem,
    priceInstallmentDisplay,
    priceUpfrontDisplay,
    priceYearlyDisplay,
    priceFixedDisplay,
    priceMonthlyDisplay,
    connectivityPriceDisplay,
    addMutation,
    updateMutation,
    buildPayload,
    buildUpdatePayload,
    resetForm,
    resetItemFields,
  };
}
